import React from 'react';
import { TagFeedback, Text } from '@spark';
import EvaluationNormalizationSummary from './EvaluationNormalizationSummary';
import ModelDetailsCodeSection from './ModelDetailsCodeSection';
import ModelDetailsSectionTitle from './ModelDetailsSectionTitle';

const FIELDS = [
  {
    key: 'systemPrompt',
    title: 'System Prompt',
    teacherKey: 'teacher_system_prompt',
    overrideKey: 'generative_student_instructions',
  },
  {
    key: 'userPrompt',
    title: 'User Prompt',
    teacherKey: 'instruction',
    overrideKey: 'generative_student_prompt',
  },
  {
    key: 'schema',
    title: 'JSON Schema',
    teacherKey: 'teacher_schema',
    overrideKey: 'generative_schema',
  },
];

/**
 * Read-only "Student Model and Prompt Setup" section for the Model Details
 * summary tab. Shown only for generative tasks (summarization / content
 * generation) when the distillation was fired with "Include prompt in
 * training & evaluation" (`include_instruction_in_training`). Mirrors the
 * Model Configuration section of the same name: each field shows the
 * resolved student value — the override snapshotted on the distillation
 * event, falling back to the matching teacher field — with a tag indicating
 * whether it was overridden or inherited from the teacher.
 */
export default function ModelDetailsStudentSetupSection({ distillationEvent }) {
  const event = distillationEvent || {};

  return (
    <div>
      <ModelDetailsSectionTitle style={{ marginBottom: 4 }}>
        Student Model and Prompt Setup
      </ModelDetailsSectionTitle>
      <Text variant="base-regular" color="secondary" as="div" style={{ marginBottom: 16 }}>
        Prompts and schema used to train and evaluate your SpecificAI model. Fields without an
        override are inherited from the teacher setup.
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {FIELDS.map((field) => {
          const override = event[field.overrideKey];
          const isOverridden = override != null;
          const value = isOverridden
            ? String(override ?? '')
            : event[field.teacherKey] || '';
          return (
            <ModelDetailsCodeSection
              key={field.key}
              title={field.title}
              titleVariant="base-semibold"
              titleExtra={
                <TagFeedback
                  label={isOverridden ? 'Overridden' : 'Inherited from teacher'}
                  variant={isOverridden ? 'processing' : 'default'}
                />
              }
              text={value}
              copyText={value}
              minHeight={80}
            />
          );
        })}
        <EvaluationNormalizationSummary source={event} />
      </div>
    </div>
  );
}
